// src/services/firebaseService.js

import { db } from "../configuration";
import {
  collection,
  addDoc,
  getDocs,
  setDoc,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  where,
  query,
  Timestamp,
  GeoPoint,
  arrayRemove,
  arrayUnion,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL, uploadBytes } from "firebase/storage";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { format } from "date-fns";
import * as XLSX from 'xlsx';

const storage = getStorage();
const auth = getAuth();

const populateRefItemWithData = async (collectionName, id) => {
  // // console.log("collectionName", collectionName);
  // // console.log("id", id);
  const r = doc(db, collectionName, id);
  const d = await getDoc(r);
  const data = d.exists() ? d.data() : null;

  return { id: d.id, ...data };
};

const formatTimestamp = (timestamp) => {
  const date = new Date(
    timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000
  );
  return format(date, "PPpp"); // Customize the format string as needed
};

export const getImageDownloadUrl = async (imagePath) => {
  const storageRef = ref(storage, imagePath);
  const url = await getDownloadURL(storageRef);
  return url;
};

export const registerUser = async (email, password, role) => {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  const user = userCredential.user;

  await updateProfile(user, { displayName: role });

  await setDoc(doc(db, "users", user.uid), {
    email: user.email,
    role: role,
    createdAt: new Date(),
  });

  return user;
};

export const createItem = async (collectionName, item) => {
  const newItem = { ...item };

  for (const key in newItem) {
    if (typeof newItem[key] === "string" && newItem[key].includes("id:")) {
      const id = newItem[key].split(":")[1];
      const refPath = `${key}s/${id}`;
      newItem[key] = doc(db, refPath);
    }
    if (key.toLowerCase().includes("date")) {
      newItem[key] = Timestamp.fromDate(new Date(newItem[key]));
    }

    // if (key === "location") {
    //   newItem[key] = new GeoPoint(
    //     newItem[key].latitude,
    //     newItem[key].longitude
    //   );
    // }
  }

  const itemRef = await addDoc(collection(db, collectionName), {
    ...newItem,
    createdAt: Timestamp.now(),
  });
  return itemRef.id;
};

export const updateItem = async (collectionName, id, updatedData) => {
  const updatedItem = { ...updatedData };

  for (const key in updatedItem) {
    if (
      typeof updatedItem[key] === "string" &&
      updatedItem[key].includes("id:")
    ) {
      const refId = updatedItem[key].split(":")[1];
      const refPath = `${key}s/${refId}`;
      updatedItem[key] = doc(db, refPath);
    }
    if (key.toLowerCase().includes("date")) {
      updatedItem[key] = Timestamp.fromDate(new Date(updatedItem[key]));
    }
  }

  const itemRef = doc(db, collectionName, id);
  await updateDoc(itemRef, updatedItem);
};

// Users collection
/**
 * Structure:
 * {
 *   id: string,         // User ID (auto-generated by Firestore)
 *   name: string,
 *   email: string,
 *   role: string,       // e.g., "student", "coordinator", "educator", "admin"
 *   department: string, // Optional field for the department if applicable
 *   createdAt: timestamp
 * }
 */

// Add a new user
export const createUser = async (user) => {
  const { email, password, role, name, phoneNumber, studentId } = user;
  // console.log("user", user);
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const authUser = userCredential.user;

    // console.log("Creating user with data:", {
    //   name,
    //   phoneNumber,
    //   studentId,
    //   email,
    //   role: doc(db, "permissions", role.toUpperCase()),
    //   createdAt: Timestamp.now(),
    // });
    // console.log("authUser", authUser);
    await setDoc(doc(db, "users", authUser.uid), {
      name,
      phoneNumber,
      studentId,
      email,
      role: doc(db, "permissions", role.toUpperCase()),
      createdAt: Timestamp.now(),
    });
    // console.log("User created successfully");

    return authUser.uid;
  } catch (error) {
    if (error.code === "auth/email-already-in-use") {
      throw new Error(
        "This email is already in use. Please use a different email."
      );
    } else {
      throw error; // Re-throw other errors
    }
  }
};

export const createUserWithoutLogout = async (user, navigate) => {
  const { email, password, role, name, baseId } = user;
  try {
    // Store current user before creating new one
    const originalUser = auth.currentUser;

    // Create a new user without affecting the current session
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const newUser = userCredential.user;

    // Create user document
    const userRef = doc(db, "users", newUser.uid);
    await setDoc(userRef, {
      name,
      email,
      role: doc(db, "permissions", role.toUpperCase()),
      createdAt: Timestamp.now(),
    });

    // Add user reference to base users array if baseId exists
    if (baseId) {
      const baseRef = doc(db, "base", baseId);
      await updateDoc(baseRef, {
        users: arrayUnion(userRef),
      });
    }

    // Restore the original user session
    await auth.updateCurrentUser(originalUser);

    // Use the navigate function passed as an argument
    navigate("/app/admin/7x4b2k-h9z83p/users");

    return newUser.uid;
  } catch (error) {
    if (error.code === "auth/email-already-in-use") {
      throw new Error(
        "This email is already in use. Please use a different email."
      );
    } else {
      throw error; // Re-throw other errors
    }
  }
};

// Get all users
export const getUsers = async () => {
  const querySnapshot = await getDocs(collection(db, "users"));
  const users = [];
  querySnapshot.forEach((doc) => {
    users.push({ id: doc.id, ...doc.data() });
  });
  return users;
};

export const getStudentUsers = async () => {
  const q = query(
    collection(db, "users"),
    where("type", "array-contains", "student")
  );
  const querySnapshot = await getDocs(q);
  const studentUsers = [];
  querySnapshot.forEach((doc) => {
    studentUsers.push({ id: doc.id, ...doc.data() });
  });
  return studentUsers;
};

// Update a user
export const updateUser = async (id, updatedData) => {
  const userRef = doc(db, "users", id);
  const permissionRef = doc(db, "permissions", updatedData.role.toUpperCase());
  updatedData.role = permissionRef;

  // Update Firestore user document
  await updateDoc(userRef, updatedData);

  // Update Firebase Authentication email if it has changed
  if (updatedData.email) {
    // console.log("updatedData.email:", updatedData.email);
    const user = auth.currentUser;
    if (user && user.uid === id) {
      await user.updateEmail(updatedData.email);
    }
  }
};

// Delete a user
export const deleteUser = async (id) => {
  const userRef = doc(db, "users", id);

  // Delete user from Firestore
  await deleteDoc(userRef);

  // Delete user from Firebase Authentication
  const user = auth.currentUser;
  if (user && user.uid === id) {
    await user.delete();
  }
};

// Rotations collection
/**
 * Structure:
 * {
 *   id: string,           // Rotation ID (auto-generated by Firestore)
 *   name: string,
 *   type: string,         // e.g., "Core", "Elective"
 *   site: string,
 *   studentId: string,    // Reference to the user (student) document
 *   startDate: timestamp,
 *   endDate: timestamp,
 *   status: string,       // e.g., "Active", "Completed", "Cancelled", "Scheduled"
 *   createdAt: timestamp
 * }
 */

// Add a new rotation
export const createRotation = async (rotation) => {
  const rotationRef = await addDoc(collection(db, "rotations"), {
    ...rotation,
    createdAt: Timestamp.now(),
  });
  return rotationRef.id;
};

// Get all rotations
export const getRotations = async () => {
  const querySnapshot = await getDocs(collection(db, "rotations"));
  const rotations = [];

  for (const d of querySnapshot.docs) {
    const rotationData = d.data();

    const locationData = await populateRefItemWithData(
      "locations",
      rotationData.location.id
    );

    rotations.push({
      id: d.id,
      ...rotationData,
      location: locationData,
      startDate: formatTimestamp(rotationData.startDate),
      endDate: rotationData.endDate
        ? formatTimestamp(rotationData.endDate)
        : null,
    });
  }

  return rotations;
};

// Get a rotation by ID
export const getRotation = async (id) => {
  const rotationRef = doc(db, "rotations", id);
  const rotationDoc = await getDoc(rotationRef);

  const oppsRef = rotationDoc.data().opportunityId;
  const oppsDoc = await getDoc(oppsRef);
  const oppsData = oppsDoc.exists() ? oppsDoc.data() : null;

  if (!rotationDoc.exists()) {
    throw new Error("Rotation not found");
  }

  const rotationData = rotationDoc.data();

  // Get location data
  const locationData = await populateRefItemWithData(
    "locations",
    oppsData.location.id
  );

  // // console.log(locationData);

  return {
    id: rotationDoc.id,
    ...rotationData,
    location: locationData,
    startDate: formatTimestamp(oppsData.startDate),
    endDate: oppsData.endDate ? formatTimestamp(oppsData.endDate) : null,
  };
};

export const getRotationsByLocationId = async (locationId) => {
  const locationRef = doc(db, "locations", locationId);
  const querySnapshot = await getDocs(
    query(collection(db, "rotations"), where("location", "==", locationRef))
  );

  const rotations = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const rotationData = doc.data();
      const [locationRef, oppsRef, studentRef] = await Promise.all([
        getDoc(rotationData.location),
        getDoc(rotationData.opps),
        getDoc(rotationData.student),
      ]);

      return {
        id: doc.id,
        ...rotationData,
        opps: oppsRef.exists()
          ? {
              id: oppsRef.id,
              ...oppsRef.data(),
              startDate: formatTimestamp(oppsRef.data().startDate),
              endDate: formatTimestamp(oppsRef.data().endDate),
            }
          : null,
        location: locationRef.exists()
          ? { id: locationRef.id, ...locationRef.data() }
          : null,
        student: studentRef.exists()
          ? { id: studentRef.id, ...studentRef.data() }
          : null,
      };
    })
  );

  return rotations;
};

export const getRotationWithReferences = async (id) => {
  const rotationRef = doc(db, "rotations", id);
  const rotationDoc = await getDoc(rotationRef);

  if (!rotationDoc.exists()) {
    throw new Error("Rotation not found");
  }

  const rotationData = rotationDoc.data();

  const locationData = await populateRefItemWithData(
    "locations",
    rotationData.location.id
  );

  const imageUrl = await getImageDownloadUrl(locationData.image);

  return {
    id: rotationDoc.id,
    ...rotationData,
    location: { ...locationData, image: imageUrl },
  };
};

// Update a rotation
export const updateRotation = async (id, updatedData) => {
  const rotationRef = doc(db, "rotations", id);
  await updateDoc(rotationRef, updatedData);
};

// Delete a rotation
export const deleteRotation = async (id) => {
  const rotationRef = doc(db, "rotations", id);
  await deleteDoc(rotationRef);
};

// Add a new location
export const createLocation = async (location) => {
  // Upload image to storage and get the download URL
  const imageRef = ref(storage, location.image.name);
  await uploadBytes(imageRef, location.image);
  const imageUrl = await getDownloadURL(imageRef);

  // Create location document in Firestore with the image URL
  const locationRef = await addDoc(collection(db, "locations"), {
    ...location,
    image: imageUrl,
    createdAt: Timestamp.now(),
  });
  return locationRef.id;
};

// Get all locations
export const getLocations = async () => {
  const querySnapshot = await getDocs(collection(db, "locations"));
  const locations = [];
  querySnapshot.forEach((doc) => {
    locations.push({ id: doc.id, ...doc.data() });
  });
  return locations;
};

// Update a location
export const updateLocation = async (id, updatedData) => {
  const locationRef = doc(db, "locations", id);
  await updateDoc(locationRef, updatedData);
};

// Delete a location
export const deleteLocation = async (id) => {
  const locationRef = doc(db, "locations", id);
  await deleteDoc(locationRef);
};

// Get location sections by location reference ID
export const getLocationSections = async (locationId) => {
  const locationRef = doc(db, "locations", locationId);
  const querySnapshot = await getDocs(
    query(collection(db, "sections"), where("location", "==", locationRef))
  );

  const locationSections = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const locationSectionData = doc.data();

      const locationData = await populateRefItemWithData(
        "locations",
        locationSectionData.location.id
      );

      const cards = await getCardsBySection(doc.id);

      return {
        id: doc.id,
        ...locationSectionData,
        cards: cards ? cards : [],
        location: locationData,
      };
    })
  );

  return locationSections;
};

export const getBaseSections = async (baseId) => {
  const baseRef = doc(db, "base", baseId);
  const querySnapshot = await getDocs(
    query(collection(db, "sections"), where("location", "==", baseRef))
  );

  const baseSections = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const baseSectionData = doc.data();
      const base = await getDoc(baseRef);

      return {
        id: doc.id,
        cards: [],
        ...baseSectionData,
        location: { id: base.id, ...base.data() },
      };
    })
  );

  return baseSections;
};

export const getLocationSectionsByLocationIdAndOrder = async (
  locationId,
  order
) => {
  const locationRef = doc(db, "locations", locationId);
  const querySnapshot = await getDocs(
    query(
      collection(db, "sections"),
      where("location", "==", locationRef),
      where("order", "==", order)
    )
  );

  const locationSections = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const locationSectionData = doc.data();

      return {
        id: doc.id,
        ...locationSectionData,
      };
    })
  );

  return locationSections;
};

export const getRequestsByLocation = async (locationId, status) => {
  const locationRef = doc(db, "locations", locationId);

  const querySnapshot = await getDocs(
    query(
      collection(db, "rotations"),
      where("locationId", "==", locationRef),
      where("currentStageOrder", "==", status)
    )
  );

  const requestsPromises = querySnapshot.docs.map(async (d) => {
    const requestData = d.data();
    const oppsData = await getDoc(requestData.opps);
    const studentData = await getDoc(requestData.student);
    const locationData = await getDoc(requestData.location);
    return {
      id: d.id,
      ...requestData,
      opps: { id: oppsData.id, ...oppsData.data() },
      student: { id: studentData.id, ...studentData.data() },
      location: { id: locationData.id, ...locationData.data() },
    };
  });

  const requests = await Promise.all(requestsPromises);
  return requests;
};

export const getRequestsByBase = async (baseId, status) => {
  const baseRef = doc(db, "base", baseId);

  const base = await getDoc(baseRef);
  const baseLocations = base
    .data()
    .locations.map((location) => doc(db, "locations", location.id));

  const querySnapshot = await getDocs(
    query(
      collection(db, "rotations"),
      where("location", "in", baseLocations),
      where("status", "==", status)
    )
  );
  const requestsPromises = querySnapshot.docs.map(async (d) => {
    const requestData = d.data();
    const oppsData = await getDoc(requestData.opps);
    const studentData = await getDoc(requestData.student);
    const locationData = await getDoc(requestData.location);
    return {
      id: d.id,
      ...requestData,
      opps: { id: oppsData.id, ...oppsData.data() },
      student: { id: studentData.id, ...studentData.data() },
      location: { id: locationData.id, ...locationData.data() },
    };
  });
  const requests = await Promise.all(requestsPromises);
  return requests;
};

export const getLocationSectionsRefByLocationIdAndOrder = async (
  locationId,
  order
) => {
  const locationRef = doc(db, "locations", locationId);
  const querySnapshot = await getDocs(
    query(
      collection(db, "sections"),
      where("location", "==", locationRef),
      where("order", "==", order)
    )
  );

  const locationSections = await Promise.all(
    querySnapshot.docs.map(async (d) => {
      return doc(db, "sections", d.id);
    })
  );

  return locationSections;
};

// Get cards by location section ID
export const getCardsBySection = async (sectionId) => {
  const sectionRef = doc(db, "sections", sectionId);

  const querySnapshot = await getDocs(
    query(collection(db, "cards"), where("section", "==", sectionRef))
  );

  const cards = await Promise.all(
    querySnapshot.docs.map(async (doc) => {
      const cardData = doc.data();

      const rotationData = await populateRefItemWithData(
        "rotations",
        cardData.rotation.id
      );

      const studentsData = await Promise.all(
        cardData.students.map(async (student) => {
          const studentData = await populateRefItemWithData(
            "users",
            student.id
          );
          return studentData;
        })
      );

      return {
        id: doc.id,
        ...cardData,
        rotation: rotationData,
        students: studentsData,
      };
    })
  );

  return cards;
};

// Cards collection
/**
 * Structure:
 * {
 *   id: string,           // Card ID (auto-generated by Firestore)
 *   rotation: ref,        // Reference to the rotation document
 *   section: ref,         // Reference to the location section document
 *   students: array,      // Array of references to the user documents
 *   otherFields: ...      // Other fields related to the card
 * }
 */

// Add a new card
export const createCard = async (card) => {
  const cardRef = await addDoc(collection(db, "cards"), {
    ...card,
    rotation: doc(db, "rotations", card.rotation),
    section: doc(db, "sections", card.section),
    students: card.students.map((studentId) => doc(db, "users", studentId)),
    createdAt: Timestamp.now(),
  });
  return cardRef.id;
};

// Get all cards
export const getCards = async () => {
  const querySnapshot = await getDocs(collection(db, "cards"));
  const cards = [];
  querySnapshot.forEach((doc) => {
    cards.push({ id: doc.id, ...doc.data() });
  });
  return cards;
};

export const getCard = async (id) => {
  const cardRef = doc(db, "rotations", id);
  const cardDoc = await getDoc(cardRef);

  if (!cardDoc.exists()) {
    throw new Error("Card not found");
  }

  const cardData = cardDoc.data();
  const oppsRef = cardData.opportunityId;
  const oppsDataa = await getDoc(oppsRef);
  const locationRef = oppsDataa.data().location;
  const studentRefs = cardData.studentId;

  const oppsDoc = await getDoc(oppsRef);
  const locationDoc = await getDoc(locationRef);
  const studentDocs = await getDoc(studentRefs);

  const oppsData = oppsDoc.exists() ? oppsDoc.data() : null;
  const locationData = locationDoc.exists() ? locationDoc.data() : null;
  const studentsData = studentDocs.exists() ? studentDocs.data() : null;

  return {
    id: cardDoc.id,
    ...cardData,
    opps: oppsData,
    location: locationData,
    student: studentsData,
  };
};

// Update a card
export const updateCard = async (id, updatedData) => {
  const cardRef = doc(db, "cards", id);
  await updateDoc(cardRef, updatedData);
};

// Delete a card
export const deleteCard = async (id) => {
  const cardRef = doc(db, "cards", id);
  await deleteDoc(cardRef);
};

export const getBaseWithLocations = async (baseId) => {
  const baseRef = doc(db, "base", baseId);
  const baseDoc = await getDoc(baseRef);
  const base = baseDoc.exists() ? baseDoc.data() : null;
  const locationPromises = base.locations.map(async (location) => {
    const data = doc(db, "locations", location.id);
    const locationData = await getDoc(data);
    return locationData.exists()
      ? { id: locationData.id, ...locationData.data() }
      : null;
  });
  const locations = await Promise.all(locationPromises);
  return { id: baseId, ...base, locations };
};

export const getAllBaseWithLocations = async () => {
  const baseRef = collection(db, "base");
  const baseSnapshot = await getDocs(baseRef);

  const basesWithLocations = await Promise.all(
    baseSnapshot.docs.map(async (baseDoc) => {
      const base = baseDoc.data();
      let locations = [];
      if (base.locations && Array.isArray(base.locations)) {
        const locationPromises = base.locations.map(async (location) => {
          if (location && location.id) {
            const locationDoc = await getDoc(doc(db, "locations", location.id));
            return locationDoc.exists()
              ? { id: locationDoc.id, ...locationDoc.data() }
              : null;
          }
          return null;
        });
        locations = (await Promise.all(locationPromises)).filter(
          (loc) => loc !== null
        );
      }
      return {
        id: baseDoc.id,
        ...base,
        locations: locations,
      };
    })
  );

  return basesWithLocations;
};

export const getOpportunities = async () => {
  const opportunitiesRef = collection(db, "opps");
  const querySnapshot = await getDocs(opportunitiesRef);
  const opportunitiesPromises = querySnapshot.docs.map(async (d) => {
    const opportunityData = d.data();
    const locationData = await getDoc(opportunityData.location);
    return {
      id: d.id,
      ...opportunityData,
      fee:
        opportunityData.fee && typeof opportunityData.fee === "string"
          ? parseFloat(opportunityData.fee.replace("SAR", "").trim())
          : opportunityData.fee,
      startDate: formatTimestamp(opportunityData.startDate),
      endDate: opportunityData.endDate
        ? formatTimestamp(opportunityData.endDate)
        : null,
      location: { id: locationData.id, ...locationData.data() },
    };
  });
  const opportunities = await Promise.all(opportunitiesPromises);
  return opportunities;
};

export const createRotationRequest = async (
  opportunityId,
  locationId,
  studentId
) => {
  const rotationRef = collection(db, "rotations");
  const opportunityRef = doc(db, "opps", opportunityId);
  const locationRef = doc(db, "locations", locationId);
  const studentRef = doc(db, "users", studentId);

  const rotationRequestRef = await addDoc(rotationRef, {
    opps: opportunityRef,
    student: studentRef,
    status: 1,
    location: locationRef,
    createdAt: Timestamp.now(),
  });
  return rotationRequestRef.id;
};

export const getRotationsByStatus = async (locationId, status) => {
  const locationRef = doc(db, "locations", locationId);
  const rotationRef = collection(db, "rotations");

  const querySnapshot = await getDocs(
    query(
      rotationRef,
      where("location", "==", locationRef),
      Array.isArray(status)
        ? where("status", "in", status)
        : where("status", "==", status)
    )
  );

  const rotations = await Promise.all(
    querySnapshot.docs.map(async (d) => {
      const opssData = await getDoc(d.data().opps);
      const locationData = await getDoc(d.data().location);
      const studentData = await getDoc(d.data().student);
      return {
        id: d.id,
        ...d.data(),
        opps: { id: opssData.id, ...opssData.data() },
        location: { id: locationData.id, ...locationData.data() },
        student: { id: studentData.id, ...studentData.data() },
      };
    })
  );
  return rotations;
};

export const getBaseRotationsByStatus = async (baseId, status) => {
  const baseRef = doc(db, "base", baseId);
  const snapShot = await getDoc(baseRef);
  const base = snapShot.exists() ? snapShot.data() : null;

  const locationPromises = base.locations.map(async (location) => {
    const data = doc(db, "locations", location.id);
    const locationData = await getDoc(data);
    return locationData.exists()
      ? { id: locationData.id, ...locationData.data() }
      : null;
  });
  const locations = await Promise.all(locationPromises);

  const rotationRef = collection(db, "rotations");
  const rotationPromises = locations.map(async (location) => {
    const locationRef = doc(db, "locations", location.id);
    const querySnapshot = await getDocs(
      query(
        rotationRef,
        where("location", "==", locationRef),
        Array.isArray(status)
          ? where("status", "in", status)
          : where("status", "==", status)
      )
    );

    const rotations = await Promise.all(
      querySnapshot.docs.map(async (d) => {
        const oppsData = await getDoc(d.data().opps);
        const oppsLocationData = await getDoc(oppsData.data().location);
        const locationData = await getDoc(d.data().location);
        const studentData = await getDoc(d.data().student);
        return {
          id: d.id,
          ...d.data(),
          opps: {
            id: oppsData.id,
            ...oppsData.data(),
            startDate: formatTimestamp(oppsData.data().startDate),
            endDate: oppsData.data().endDate
              ? formatTimestamp(oppsData.data().endDate)
              : null,
            location: { id: oppsLocationData.id, ...oppsLocationData.data() },
          },
          location: { id: locationData.id, ...locationData.data() },
          student: { id: studentData.id, ...studentData.data() },
        };
      })
    );
    return rotations;
  });

  const rotationsList = (await Promise.all(rotationPromises)).flat();
  return rotationsList;
};

export const getStudentRotations = async (studentId) => {
  const studentRef = doc(db, "users", studentId);
  const querySnapshot = await getDocs(
    query(collection(db, "rotations"), where("studentId", "==", studentRef))
  );

  const rotations = await Promise.all(
    querySnapshot.docs.map(async (d) => {
      const oppsData = await getDoc(d.data().opportunityId);
      const oppsLocationData = await getDoc(oppsData.data().location);
      // const locationData = await getDoc(d.data().location);
      const studentData = await getDoc(d.data().studentId);
      return {
        id: d.id,
        ...d.data(),
        opps: {
          id: oppsData.id,
          ...oppsData.data(),
          startDate: formatTimestamp(oppsData.data().startDate),
          endDate: oppsData.data().endDate
            ? formatTimestamp(oppsData.data().endDate)
            : null,
          location: { id: oppsLocationData.id, ...oppsLocationData.data() },
        },
        location: { id: oppsLocationData.id, ...oppsLocationData.data() },
        student: { id: studentData.id, ...studentData.data() },
      };
    })
  );

  return rotations;
};

export const uploadImage = async (file) => {
  const storage = getStorage();
  const storageRef = ref(storage, `images/${file.name}`);

  try {
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading image: ", error);
    throw error;
  }
};

// Create a new base
export const createBase = async (baseData) => {
  try {
    const baseRef = await addDoc(collection(db, "base"), {
      ...baseData,
      createdAt: Timestamp.now(),
    });
    // console.log("Base created successfully with ID: ", baseRef.id);
    return baseRef.id;
  } catch (error) {
    console.error("Error creating base: ", error);
    throw error;
  }
};

// Update a base
export const updateBase = async (id, updatedData) => {
  const baseRef = doc(db, "base", id);
  try {
    await updateDoc(baseRef, updatedData);
    // console.log("Base updated successfully");
  } catch (error) {
    console.error("Error updating base: ", error);
    throw error;
  }
};

// Delete a base
export const deleteBase = async (id) => {
  const baseRef = doc(db, "base", id);
  try {
    await deleteDoc(baseRef);
    // console.log("Base deleted successfully");
  } catch (error) {
    console.error("Error deleting base: ", error);
    throw error;
  }
};

// Get all locations
export const getAllLocations = async () => {
  const querySnapshot = await getDocs(collection(db, "locations"));
  const locations = [];
  querySnapshot.forEach((doc) => {
    locations.push({ id: doc.id, ...doc.data() });
  });
  return locations;
};

// // Get locations by base ID
// export const getLocationsByBaseId = async (baseId) => {
//   const baseRef = doc(db, "base", baseId);
//   const baseDoc = await getDoc(baseRef);
//   const baseData = baseDoc.exists() ? baseDoc.data() : null;

//   if (!baseData || !baseData.locations) {
//     return [];
//   }

//   const locationPromises = baseData.locations.map(async (location) => {
//     const locationDoc = await getDoc(doc(db, "locations", location.id));
//     return locationDoc.exists()
//       ? { id: locationDoc.id, ...locationDoc.data() }
//       : null;
//   });

//   const locations = await Promise.all(locationPromises);
//   return locations.filter((location) => location !== null);
// };

// Get locations by user ID
export const getLocationsByUserId = async (userId) => {
  const userRef = doc(db, "users", userId);
  const querySnapshot = await getDocs(
    query(
      collection(db, "locations"),
      where("users", "array-contains", userRef)
    )
  );

  const locations = [];
  querySnapshot.forEach((doc) => {
    locations.push({ id: doc.id, ...doc.data() });
  });
  // console.log(locations);
  return locations;
};

// Create a new location
export const createLocation2 = async (locationData) => {
  const { name, location, image, base, mapUrl } = locationData;
  // console.log("Creating location with data:", locationData);
  const baseRef = doc(db, "base", base);
  const newLocation = {
    name,
    location: new GeoPoint(location.latitude, location.longitude),
    image,
    base: baseRef,
    createdAt: Timestamp.now(),
    mapUrl: mapUrl,
  };

  const locationRef = await addDoc(collection(db, "locations"), newLocation);

  // Update the base document to include the new location reference
  await updateBaseLocations(base, locationRef.id, "add");

  // Copy the template lifecycle and create new one for this location
  const templateLifecycleRef = doc(db, "lifecycle-templates", "BASIC");
  const templateLifecycle = await getDoc(templateLifecycleRef);

  if (templateLifecycle.exists()) {
    const lifecycleData = templateLifecycle.data();
    await addDoc(collection(db, "lifecycles"), {
      ...lifecycleData,
      location: doc(db, "locations", locationRef.id),
      locationId: locationRef.id,
      createdAt: Timestamp.now(),
      name: `${name} Basic`,
    });
  }

  return locationRef.id;
};

// Update a location
export const updateLocation2 = async (id, locationData) => {
  const { name, location, image, base, mapUrl } = locationData;
  const baseRef = base;
  const updatedLocation = {
    name,
    location: new GeoPoint(location.latitude, location.longitude),
    image,
    base: baseRef,
    mapUrl,
  };

  const locationRef = doc(db, "locations", id);
  const oldLocation = await getDoc(locationRef);
  const oldBaseId = oldLocation.data().base.id;

  await updateDoc(locationRef, updatedLocation);

  // If the base has changed, update both the old and new base documents
  if (oldBaseId !== base.id) {
    await updateBaseLocations(oldBaseId, id, "remove");
    await updateBaseLocations(base, id, "add");
  }
};

// Delete a location
export const deleteLocation2 = async (id) => {
  const locationRef = doc(db, "locations", id);
  const location = await getDoc(locationRef);
  const baseId = location.data().base.id;

  await deleteDoc(locationRef);

  // Remove the location reference from the base document
  await updateBaseLocations(baseId, id, "remove");
};

// Get all bases (universities)
export const getAllBases = async () => {
  const querySnapshot = await getDocs(collection(db, "base"));
  const bases = [];
  querySnapshot.forEach((doc) => {
    bases.push({ id: doc.id, ...doc.data() });
  });
  return bases;
};

// Get all opportunities
export const getAllOpps = async () => {
  const oppsRef = collection(db, "opps");
  const querySnapshot = await getDocs(oppsRef);
  const opps = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    opps.push({
      id: doc.id,
      ...data,
      startDate: data.startDate.toDate().toISOString().slice(0, 16),
      endDate: data.endDate.toDate().toISOString().slice(0, 16),
      location: data.location.id,
      form: data.form?.id || "",
    });
  });
  return opps;
};

// Get opportunities by base ID
export const getOppsByBaseID = async (baseId) => {
  // console.log(baseId);
  const oppsRef = collection(db, "opps");
  const baseRef = doc(db, "base", baseId);
  const querySnapshot = await getDocs(
    query(oppsRef, where("base", "==", baseRef))
  );
  const opps = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    opps.push({
      id: doc.id,
      ...data,
      startDate: data.startDate.toDate().toISOString().slice(0, 16),
      endDate: data.endDate.toDate().toISOString().slice(0, 16),
      location: data.location.id,
      form: data.form?.id || "",
      baseId: baseId,
    });
  });
  return opps;
};

// Create a new opportunity
export const createOpp = async (opp) => {
  const oppsRef = collection(db, "opps");
  const locationRef = doc(db, "locations", opp.location);
  const formRef = opp.form ? doc(db, "forms", opp.form) : null;
  const baseRef = doc(db, "base", opp.baseId);
  const newOpp = {
    ...opp,
    startDate: Timestamp.fromDate(new Date(opp.startDate)),
    endDate: Timestamp.fromDate(new Date(opp.endDate)),
    location: locationRef,
    form: formRef,
    createdAt: Timestamp.now(),
    base: baseRef,
  };
  const docRef = await addDoc(oppsRef, newOpp);
  return docRef.id;
};

// Update an opportunity
export const updateOpp = async (id, updatedOpp) => {
  // console.log("updatedOpp", updatedOpp);
  const oppRef = doc(db, "opps", id);
  const locationRef = doc(db, "locations", updatedOpp.location);
  const formRef = updatedOpp.form ? doc(db, "forms", updatedOpp.form) : null;
  const oppToUpdate = {
    ...updatedOpp,
    startDate: Timestamp.fromDate(new Date(updatedOpp.startDate)),
    endDate: Timestamp.fromDate(new Date(updatedOpp.endDate)),
    location: locationRef,
    form: formRef,
    base: updatedOpp.base,
    updatedAt: Timestamp.now(),
    updatedBy: auth.currentUser.uid,
  };
  await updateDoc(oppRef, oppToUpdate);
};

// Delete an opportunity
export const deleteOpp = async (id) => {
  const oppRef = doc(db, "opps", id);
  await updateDoc(oppRef, {
    base: "",
    baseId: "",
    seats: 0,
    isDeleted: true,
    deletedAt: Timestamp.now(),
    deletedBy: auth.currentUser.uid,
    teachers: [],
  });
};

// Get all users
export const getAllUsers = async () => {
  const usersRef = collection(db, "users");
  const querySnapshot = await getDocs(usersRef);
  const users = [];

  for (const doc of querySnapshot.docs) {
    const userData = doc.data();
    let roleData = null;

    if (userData.role && typeof userData.role === "object") {
      const roleDoc = await getDoc(userData.role);
      if (roleDoc.exists()) {
        roleData = { id: roleDoc.id, ...roleDoc.data() };
      }
    }

    users.push({
      id: doc.id,
      ...userData,
      role: roleData,
    });
  }

  return users;
};

// Get users by base ID
export const getUsersByBaseId = async (baseId) => {
  try {
    // Get the base document first
    const baseRef = doc(db, "base", baseId);
    const baseDoc = await getDoc(baseRef);

    if (!baseDoc.exists() || !baseDoc.data().users) {
      return [];
    }

    // Get all users with ADMIN or TRAINER permission that are in the base's users array
    const adminRef = doc(db, "permissions", "ADMIN");
    const trainerRef = doc(db, "permissions", "TRAINER");

    const usersQuery = query(
      collection(db, "users"),
      where("role", "in", [adminRef, trainerRef]),
      where(
        "__name__",
        "in",
        baseDoc.data().users.map((userRef) => userRef.id)
      )
    );

    const usersSnapshot = await getDocs(usersQuery);
    const users = await Promise.all(
      usersSnapshot.docs.map(async (doc) => {
        const userData = doc.data();
        let roleData = null;

        if (userData.role && typeof userData.role === "object") {
          const roleDoc = await getDoc(userData.role);
          if (roleDoc.exists()) {
            roleData = { id: roleDoc.id, ...roleDoc.data() };
          }
        }

        return {
          id: doc.id,
          ...userData,
          role: roleData,
        };
      })
    );

    return users;
  } catch (error) {
    console.error("Error getting users by base ID:", error);
    throw error;
  }
};

// Create a new user
// export const createUser = async (user) => {
//   const usersRef = collection(db, "users");
//   const newUser = {
//     ...user,
//     createdAt: Timestamp.now(),
//   };
//   const docRef = await addDoc(usersRef, newUser);
//   return docRef.id;
// };

// // Update a user
// export const updateUser = async (id, updatedUser) => {
//   const userRef = doc(db, "users", id);
//   await updateDoc(userRef, updatedUser);
// };

// // Delete a user
// export const deleteUser = async (id) => {
//   const userRef = doc(db, "users", id);
//   await deleteDoc(userRef);
// };

// Get a single user by ID
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("User not found");
    }

    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// Get all rotations
export const getAllRotations = async () => {
  const rotationsRef = collection(db, "rotations");
  const querySnapshot = await getDocs(rotationsRef);
  const rotations = [];
  querySnapshot.forEach((doc) => {
    rotations.push({
      id: doc.id,
      ...doc.data(),
      location: doc.data().location.id,
      opps: doc.data().opps.id,
      student: doc.data().student.id,
    });
  });
  return rotations;
};

// Create a new rotation
export const createRotation2 = async (rotation) => {
  const rotationsRef = collection(db, "rotations");
  const locationRef = doc(db, "locations", rotation.location);
  const oppsRef = doc(db, "opps", rotation.opps);
  const studentRef = doc(db, "users", rotation.student);
  const newRotation = {
    ...rotation,
    location: locationRef,
    opps: oppsRef,
    student: studentRef,
    createdAt: Timestamp.now(),
  };
  const docRef = await addDoc(rotationsRef, newRotation);
  return docRef.id;
};

// Update a rotation
export const updateRotation2 = async (id, updatedRotation) => {
  const rotationRef = doc(db, "rotations", id);
  const locationRef = doc(db, "locations", updatedRotation.location);
  const oppsRef = doc(db, "opps", updatedRotation.opps);
  const studentRef = doc(db, "users", updatedRotation.student);
  const rotationToUpdate = {
    ...updatedRotation,
    location: locationRef,
    opps: oppsRef,
    student: studentRef,
  };
  await updateDoc(rotationRef, rotationToUpdate);
};

// Delete a rotation
export const deleteRotation2 = async (id) => {
  const rotationRef = doc(db, "rotations", id);
  await deleteDoc(rotationRef);
};

// Helper function to update the locations array in the base document
const updateBaseLocations = async (baseId, locationId, action) => {
  const baseRef = doc(db, "base", baseId);
  const locationRef = doc(db, "locations", locationId);

  if (action === "add") {
    await updateDoc(baseRef, {
      locations: arrayUnion(locationRef),
    });
  } else if (action === "remove") {
    await updateDoc(baseRef, {
      locations: arrayRemove(locationRef),
    });
  }
};

// Evaluations collection
/**
 * Structure:
 * {
 *   id: string,           // Evaluation ID (auto-generated by Firestore)
 *   studentId: string,    // Reference to the student user document
 *   evaluatorId: string,  // Reference to the evaluator user document
 *   rotationId: string,   // Reference to the rotation document
 *   locationId: string,   // Reference to the location document
 *   opportunityId: string, // Reference to the opportunity document
 *   evaluationDate: timestamp,
 *   categories: [
 *     {
 *       categoryName: string,
 *       questions: [
 *         {
 *           questionId: string,
 *           questionText: string,
 *           selectedOption: string
 *         }
 *       ]
 *     }
 *   ],
 *   overallFeedback: string,
 *   evaluationScore: number,
 *   createdAt: timestamp
 * }
 */

// Get evaluation for a specific rotation
export const getEvaluation = async (rotationId) => {
  // // console.log(rotationId);
  const evaluationRef = collection(db, "evaluations");
  const q = query(evaluationRef, where("rotationId", "==", rotationId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const doc = querySnapshot.docs[0];
  return { id: doc.id, ...doc.data() };
};

export const getEvaluationForCard = async (evaluationId, rotationId) => {
  try {
    const evaluationDoc = await getDoc(doc(db, "evaluations", evaluationId));
    if (!evaluationDoc.exists()) {
      throw new Error("Evaluation not found");
    }

    return {
      id: evaluationDoc.id,
      ...evaluationDoc.data(),
      rotationId,
    };
  } catch (error) {
    console.error("Error fetching evaluation:", error);
    throw error;
  }
};

// Get evaluation questions
export const getQuestions = async () => {
  const questionsRef = collection(db, "questions");
  const querySnapshot = await getDocs(questionsRef);

  const allQuestions = [];
  querySnapshot.forEach((doc) => {
    allQuestions.push({ id: doc.id, ...doc.data() });
  });

  if (allQuestions.length > 0) {
    return allQuestions;
  } else {
    // console.log("No questions found!");
    return [];
  }
};

// Create a new evaluation
export const createEvaluation = async (evaluation) => {
  const evaluationRef = await addDoc(collection(db, "evaluations"), {
    ...evaluation,
    createdAt: Timestamp.now(),
  });
  return evaluationRef.id;
};

// Update an existing evaluation
export const updateEvaluation = async (id, updatedData) => {
  const evaluationRef = doc(db, "evaluations", id);
  await updateDoc(evaluationRef, updatedData);
};

// Delete an evaluation
export const deleteEvaluation = async (id) => {
  const evaluationRef = doc(db, "evaluations", id);
  await deleteDoc(evaluationRef);
};

// Reports

// Get rotation applications count
export const getRotationApplicationsCount = async () => {
  const rotationsRef = collection(db, "rotations");
  const querySnapshot = await getDocs(rotationsRef);
  const promises = querySnapshot.docs.map(async (doc) => {
    const data = doc.data();
    const promises = [getDoc(data.studentId), getDoc(data.opportunityId)];

    if (data.locationId) {
      promises.push(getDoc(data.locationId));
    }

    const [studentData, oppsData, locationData] = await Promise.all(promises);

    const student = studentData.exists() ? studentData.data() : null;
    const opps = oppsData.exists() ? oppsData.data() : null;
    const location = locationData?.exists() ? locationData.data() : null;

    return {
      id: doc.id,
      studentName: student?.name || "N/A",
      oppsName: opps?.name || "N/A",
      locationName: location?.name || "N/A",
      oppsStartDate: opps?.startDate ? formatTimestamp(opps.startDate) : "N/A",
      oppsEndDate: opps?.endDate ? formatTimestamp(opps.endDate) : "N/A",
      createdAt: formatTimestamp(data.createdAt),
    };
  });

  const rotations = await Promise.all(promises);
  return rotations;
};

// Get students applied count
export const getStudentsAppliedCount = async () => {
  const studentsRef = collection(db, "users");
  const studentRoleRef = doc(db, "permissions", "STUDENT");
  const q = query(studentsRef, where("role", "==", studentRoleRef));
  const querySnapshot = await getDocs(q);
  const students = [];
  querySnapshot.forEach((doc) => {
    students.push({
      id: doc.id,
      name: doc.data().name,
      role: doc.data().role.id,
      type: doc.data().type,
    });
  });
  return students;
};

// Get accepted rotations count
export const getAcceptedRotationsCount = async () => {
  const rotationsRef = collection(db, "rotations");
  const q = query(rotationsRef, where("currentStageOrder", ">", 1));
  const querySnapshot = await getDocs(q);
  const promises = querySnapshot.docs.map(async (doc) => {
    const data = doc.data();
    const promises = [getDoc(data.studentId), getDoc(data.opportunityId)];

    if (data.locationId) {
      promises.push(getDoc(data.locationId));
    }

    const [studentData, oppsData, locationData] = await Promise.all(promises);

    const student = studentData.exists() ? studentData.data() : null;
    const opps = oppsData.exists() ? oppsData.data() : null;
    const location = locationData?.exists() ? locationData.data() : null;

    return {
      id: doc.id,
      studentName: student?.name || "N/A",
      oppsName: opps?.name || "N/A",
      locationName: location?.name || "N/A",
      oppsStartDate: opps?.startDate ? formatTimestamp(opps.startDate) : "N/A",
      oppsEndDate: opps?.endDate ? formatTimestamp(opps.endDate) : "N/A",
      createdAt: formatTimestamp(data.createdAt),
      status: data.status,
    };
  });

  const acceptedRotations = await Promise.all(promises);
  // console.log("acceptedRotations", acceptedRotations);
  return acceptedRotations;
};

// Get students with accepted rotations count
export const getStudentsWithAcceptedRotationsCount = async () => {
  const rotationsRef = collection(db, "rotations");
  const q = query(rotationsRef, where("currentStageOrder", ">", 1));
  const querySnapshot = await getDocs(q);

  const uniqueStudents = new Map();
  const promises = querySnapshot.docs.map(async (doc) => {
    const data = doc.data();
    const studentData = await getDoc(data.studentId);
    const student = studentData.exists() ? studentData.data() : null;
    if (!uniqueStudents.has(data.studentId.id)) {
      uniqueStudents.set(data.studentId.id, {
        id: data.studentId.id,
        rotations: [],
      });
    }
    uniqueStudents
      .get(data.studentId.id)
      .rotations.push({ id: doc.id, studentName: student?.name || "N/A" });
  });

  await Promise.all(promises);

  // Convert the Map to an array and format the data
  const formattedData = Array.from(uniqueStudents.values()).map((student) => ({
    id: student.id,
    studentName: student.rotations[0]?.studentName || "N/A",
    rotationCount: student.rotations.length,
  }));

  // Sort the data by rotation count in descending order
  formattedData.sort((a, b) => b.rotationCount - a.rotationCount);

  return Array.from(formattedData.values());
};

// Get student, faculty, and preceptors report
export const getUsersReport = async () => {
  const usersRef = collection(db, "users");
  const querySnapshot = await getDocs(usersRef);

  const report = {
    students: 0,
    faculty: 0,
    preceptors: 0,
  };

  querySnapshot.forEach((doc) => {
    const role = doc.data().role;
    if (role.includes("student")) report.students++;
    else if (role.includes("faculty")) report.faculty++;
    else if (role.includes("preceptor")) report.preceptors++;
  });

  return report;
};

// Get performance and utilization report
export const getPerformanceUtilizationReport = async () => {
  const rotationsRef = collection(db, "rotations");
  const querySnapshot = await getDocs(rotationsRef);

  let totalDuration = 0;
  let completedRotations = 0;

  for (const doc of querySnapshot.docs) {
    const rotation = doc.data();
    if (rotation.currentStageOrder === 5) {
      completedRotations++;
      const oppsRef = rotation.opps;
      const oppsDoc = await getDoc(oppsRef);
      if (oppsDoc.exists()) {
        const oppsData = oppsDoc.data();
        const start = oppsData.startDate.toDate();
        const end = oppsData.endDate.toDate();
        const duration = (end - start) / (1000 * 60 * 60 * 24); // Duration in days
        totalDuration += duration;
      }
    }
  }

  const averageDuration =
    completedRotations > 0 ? totalDuration / completedRotations : 0;

  return [
    {
      completedRotations,
      averageDuration: Math.round(averageDuration * 10) / 10, // Round to 1 decimal place
    },
  ];
};

// Get student, faculty, and preceptors report
export const getStudentFacultyPreceptorsReport = async () => {
  const usersRef = collection(db, "users");
  const querySnapshot = await getDocs(usersRef);

  const report = [];
  const roleCounts = {
    student: 0,
    faculty: 0,
    trainer: 0,
  };

  for (const doc of querySnapshot.docs) {
    const userData = doc.data();
    const roleRef = userData.role;
    const roleDoc = await getDoc(roleRef);
    const roleId = roleDoc.id;

    if (roleId === "STUDENT") {
      roleCounts.student++;
    } else if (roleId === "INSTITUTION") {
      roleCounts.faculty++;
    } else if (roleId === "TRAINER") {
      roleCounts.trainer++;
    }

    report.push({
      id: doc.id,
      name: userData.name || "N/A",
      email: userData.email || "N/A",
      role: roleDoc.id || "N/A",
    });
  }

  report.push({
    id: "total",
    name: "Total Counts",
    email: "",
    role: `Students: ${roleCounts.student}, Faculty: ${roleCounts.faculty}, Trainers: ${roleCounts.trainer}`,
  });

  return report;
};

/**
 * Structure:
 * {
 *   id: string,           // Document ID (auto-generated by Firestore)
 *   userId: string,       // Reference to the user document
 *   documentType: string, // e.g., "Compliance", "Report"
 *   url: string,          // URL of the uploaded document
 *   status: string,       // e.g., "Pending", "Verified", "Expired"
 *   expiryDate: timestamp, // Optional field if applicable
 *   createdAt: timestamp
 * }
 */

// Add a new document
export const createDocument = async (document) => {
  const documentRef = await addDoc(collection(db, "documents"), {
    ...document,
    createdAt: Timestamp.now(),
  });
  return documentRef.id;
};

// Get all documents
export const getDocuments = async () => {
  const querySnapshot = await getDocs(collection(db, "documents"));
  const documents = [];
  querySnapshot.forEach((doc) => {
    documents.push({ id: doc.id, ...doc.data() });
  });
  return documents;
};

// Update a document
export const updateDocument = async (id, updatedData) => {
  const documentRef = doc(db, "documents", id);
  await updateDoc(documentRef, updatedData);
};

// Delete a document
export const deleteDocument = async (id) => {
  const documentRef = doc(db, "documents", id);
  await deleteDoc(documentRef);
};

// Messages collection
/**
 * Structure:
 * {
 *   id: string,           // Message ID (auto-generated by Firestore)
 *   senderId: string,     // Reference to the user (sender) document
 *   receiverId: string,   // Reference to the user (receiver) document
 *   content: string,
 *   timestamp: timestamp,
 *   read: boolean         // Whether the message has been read
 * }
 */

// Add a new message
export const createMessage = async (message) => {
  const messageRef = await addDoc(collection(db, "messages"), {
    ...message,
    timestamp: Timestamp.now(),
    read: false,
  });
  return messageRef.id;
};

// Get all messages
export const getMessages = async () => {
  const querySnapshot = await getDocs(collection(db, "messages"));
  const messages = [];
  querySnapshot.forEach((doc) => {
    messages.push({ id: doc.id, ...doc.data() });
  });
  return messages;
};

// Update a message
export const updateMessage = async (id, updatedData) => {
  const messageRef = doc(db, "messages", id);
  await updateDoc(messageRef, updatedData);
};

// Delete a message
export const deleteMessage = async (id) => {
  const messageRef = doc(db, "messages", id);
  await deleteDoc(messageRef);
};

// Compliance collection
/**
 * Structure:
 * {
 *   id: string,           // Compliance report ID (auto-generated by Firestore)
 *   userId: string,       // Reference to the user document
 *   reportType: string,   // e.g., "Performance", "Rotation"
 *   details: string,      // Detailed report or summary
 *   createdAt: timestamp
 * }
 */

// Add a new compliance report
export const createComplianceReport = async (report) => {
  const reportRef = await addDoc(collection(db, "compliance"), {
    ...report,
    createdAt: Timestamp.now(),
  });
  return reportRef.id;
};

// Get all compliance reports
export const getComplianceReports = async () => {
  const querySnapshot = await getDocs(collection(db, "compliance"));
  const reports = [];
  querySnapshot.forEach((doc) => {
    reports.push({ id: doc.id, ...doc.data() });
  });
  return reports;
};

// Update a compliance report
export const updateComplianceReport = async (id, updatedData) => {
  const reportRef = doc(db, "compliance", id);
  await updateDoc(reportRef, updatedData);
};

// Delete a compliance report
export const deleteComplianceReport = async (id) => {
  const reportRef = doc(db, "compliance", id);
  await deleteDoc(reportRef);
};

// Get all forms
export const getAllForms = async () => {
  try {
    const formsCollection = collection(db, "forms");
    const querySnapshot = await getDocs(formsCollection);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting forms: ", error);
    throw error;
  }
};

// Get forms by base ID
export const getFormsByBaseId = async (baseId) => {
  try {
    const q = query(
      collection(db, "forms"),
      where("base", "==", doc(db, "base", baseId))
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting forms by base ID:", error);
    throw error;
  }
};

// Create a new form
export const createForm = async (formData) => {
  try {
    const formsCollection = collection(db, "forms");
    const baseRef = doc(db, "base", formData.baseId);
    const docRef = await addDoc(formsCollection, {
      name: formData.name,
      questions: formData.questions.map((q) => ({
        text: q.text,
        requiresFile: q.requiresFile || false,
      })),
      createdAt: new Date().toISOString(),
      base: baseRef,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating form: ", error);
    throw error;
  }
};

// Update an existing form
export const updateForm = async (formId, formData) => {
  try {
    const formRef = doc(db, "forms", formId);
    const baseRef = doc(db, "base", formData.baseId);
    await updateDoc(formRef, {
      name: formData.name,
      questions: formData.questions.map((q) => ({
        text: q.text,
        requiresFile: q.requiresFile || false,
      })),
      updatedAt: new Date().toISOString(),
      base: baseRef,
    });
  } catch (error) {
    console.error("Error updating form: ", error);
    throw error;
  }
};

// Delete a form
export const deleteForm = async (formId) => {
  try {
    // Get the form first to get its base ID
    const formRef = doc(db, "forms", formId);
    const formDoc = await getDoc(formRef);
    const baseId = formDoc.data().base.id;

    // Get all opps with this base ID
    const oppsRef = collection(db, "opps");
    const q = query(oppsRef, where("base", "==", doc(db, "base", baseId)));
    const oppsSnapshot = await getDocs(q);

    // Update all matching opps to remove form reference
    const oppsUpdates = oppsSnapshot.docs.map(async (oppDoc) => {
      const oppRef = doc(db, "opps", oppDoc.id);
      await updateDoc(oppRef, {
        form: "",
      });
    });

    // Wait for all opp updates to complete
    await Promise.all(oppsUpdates);

    // Update the form instead of deleting it
    await updateDoc(formRef, {
      base: "",
      location: "",
      isDeleted: true,
      deletedAt: Timestamp.now(),
      deletedBy: auth.currentUser.uid,
    });
  } catch (error) {
    console.error("Error deleting form: ", error);
    throw error;
  }
};

// Lifecycle Management
export const getAllLifecycles = async () => {
  try {
    const lifecyclesCollection = collection(db, "lifecycles");
    const querySnapshot = await getDocs(lifecyclesCollection);

    // Map through documents and resolve task references
    const lifecycles = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data();

        // Resolve tasks for each stage
        const stages = await Promise.all(
          data.stages.map(async (stage) => {
            const tasks = await Promise.all(
              (stage.tasks || []).map(async (taskRef) => {
                const taskDoc = await getDoc(taskRef);
                return taskDoc.exists()
                  ? { id: taskDoc.id, ...taskDoc.data() }
                  : null;
              })
            );

            return {
              ...stage,
              tasks: tasks.filter((task) => task !== null), // Remove any null tasks
            };
          })
        );

        return {
          id: doc.id,
          ...data,
          stages,
        };
      })
    );

    return lifecycles;
  } catch (error) {
    console.error("Error getting lifecycles: ", error);
    throw error;
  }
};

export const createLifecycle = async (lifecycleData) => {
  try {
    const lifecyclesCollection = collection(db, "lifecycles");
    const locationRef = doc(db, "locations", lifecycleData.locationId);
    const baseRef = doc(db, "base", lifecycleData.baseId);
    // Map stages to include task references
    const stages = lifecycleData.stages.map((stage) => ({
      name: stage.name,
      order: stage.order,
      color: stage.color,
      evaluation: stage.evaluation,
      tasks: stage.tasks
        ? stage.tasks.map((taskId) => doc(db, "tasks", taskId))
        : [], // Convert task IDs to references
    }));

    const docRef = await addDoc(lifecyclesCollection, {
      name: lifecycleData.name,
      locationId: lifecycleData.locationId, // Store locationId directly instead of reference
      location: locationRef, // Add location reference
      stages: stages,
      createdAt: Timestamp.now(),
      base: baseRef,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating lifecycle: ", error);
    throw error;
  }
};

export const updateLifecycle = async (lifecycleId, lifecycleData) => {
  try {
    const baseRef = doc(db, "base", lifecycleData.baseId);
    const lifecycleRef = doc(db, "lifecycles", lifecycleId);
    const locationRef = doc(db, "locations", lifecycleData.locationId);
    // Map stages to include task references
    const stages = lifecycleData.stages.map((stage) => ({
      name: stage.name,
      order: stage.order,
      color: stage?.color,
      evaluation: stage?.evaluation || false,
      tasks: stage.tasks
        ? stage.tasks.map((taskId) => doc(db, "tasks", taskId))
        : [],
    }));

    await updateDoc(lifecycleRef, {
      name: lifecycleData.name,
      locationId: lifecycleData.locationId, // Store locationId directly instead of reference
      location: locationRef, // Add location reference
      stages: stages,
      updatedAt: Timestamp.now(),
      base: baseRef,
    });
  } catch (error) {
    console.error("Error updating lifecycle: ", error);
    throw error;
  }
};

export const deleteLifecycle = async (lifecycleId) => {
  try {
    const lifecycleRef = doc(db, "lifecycles", lifecycleId);
    await deleteDoc(lifecycleRef);
  } catch (error) {
    console.error("Error deleting lifecycle: ", error);
    throw error;
  }
};

// Tasks Management
export const getAllTasks = async () => {
  try {
    const tasksCollection = collection(db, "tasks");
    const querySnapshot = await getDocs(tasksCollection);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting tasks: ", error);
    throw error;
  }
};

export const getTasksByBaseId = async (baseId) => {
  try {
    const tasksCollection = collection(db, "tasks");
    const baseRef = doc(db, "base", baseId);
    const q = query(tasksCollection, where("base", "==", baseRef));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting tasks by base ID: ", error);
    throw error;
  }
};

export const createTask = async (taskData) => {
  try {
    const tasksCollection = collection(db, "tasks");
    const baseRef = doc(db, "base", taskData.baseId);
    const docRef = await addDoc(tasksCollection, {
      name: taskData.name,
      questions: taskData.questions, // Array of question objects
      createdAt: Timestamp.now(),
      base: baseRef,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating task: ", error);
    throw error;
  }
};

export const updateTask = async (taskId, taskData) => {
  try {
    const baseRef = doc(db, "base", taskData.baseId);
    const taskRef = doc(db, "tasks", taskId);
    await updateDoc(taskRef, {
      name: taskData.name,
      questions: taskData.questions,
      updatedAt: Timestamp.now(),
      base: baseRef,
    });
  } catch (error) {
    console.error("Error updating task: ", error);
    throw error;
  }
};

export const deleteTask = async (taskId) => {
  try {
    const taskRef = doc(db, "tasks", taskId);
    const taskDoc = await getDoc(taskRef);
    const taskData = taskDoc.data();
    const baseId = taskData?.base?.id;

    if (baseId) {
      // Get all lifecycles in the base
      const lifecyclesQuery = query(
        collection(db, "lifecycles"),
        where("base", "==", doc(db, "base", baseId))
      );
      const lifecyclesSnapshot = await getDocs(lifecyclesQuery);

      // Batch write for better performance and atomicity
      const batch = writeBatch(db);

      // Update each lifecycle that contains this task
      for (const lifecycleDoc of lifecyclesSnapshot.docs) {
        const lifecycleData = lifecycleDoc.data();
        let updated = false;

        // Update stages array by removing task reference
        const updatedStages = lifecycleData.stages.map((stage) => ({
          ...stage,
          tasks: (stage.tasks || []).filter((task) => task.id !== taskId),
        }));

        if (updated) {
          batch.update(doc(db, "lifecycles", lifecycleDoc.id), {
            stages: updatedStages,
          });
        }
      }
      // Update the task document to remove base and locationId
      batch.update(taskRef, {
        base: "",
        locationId: "",
        updatedAt: Timestamp.now(),
        isDeleted: true,
        deletedAt: Timestamp.now(),
        deletedBy: doc(db, "users", auth.currentUser.uid),
      });

      // Commit all the batch operations
      await batch.commit();
    }
  } catch (error) {
    console.error("Error updating task references: ", error);
    throw error;
  }
};

// Add these new functions to handle form submissions

export const createFormSubmission = async (submission) => {
  try {
    const submissionRef = await addDoc(collection(db, "form-submissions"), {
      ...submission,
      createdAt: Timestamp.now(),
    });
    return submissionRef.id;
  } catch (error) {
    console.error("Error creating form submission:", error);
    throw error;
  }
};

export const getFormSubmission = async (submissionId) => {
  try {
    const submissionRef = doc(db, "form-submissions", submissionId);
    const submissionDoc = await getDoc(submissionRef);
    if (submissionDoc.exists()) {
      return { id: submissionDoc.id, ...submissionDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting form submission:", error);
    throw error;
  }
};

export const getFormSubmissionsByOpportunity = async (opportunityId) => {
  try {
    const q = query(
      collection(db, "form-submissions"),
      where("opportunityId", "==", doc(db, "opps", opportunityId))
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting form submissions:", error);
    throw error;
  }
};

// Form-related functions
export const getFormById = async (formId) => {
  try {
    const formRef = doc(db, "forms", formId);
    const formDoc = await getDoc(formRef);
    if (formDoc.exists()) {
      return { id: formDoc.id, ...formDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching form:", error);
    throw error;
  }
};

export const submitFormWithFiles = async (formData) => {
  try {
    const { opportunityId, studentId, formId, answers, files = {} } = formData; // Default files to empty object if not provided
    const storage = getStorage();

    // 2. Get opportunity's location
    const oppRef = doc(db, "opps", opportunityId);
    const oppDoc = await getDoc(oppRef);
    const locationRef = oppDoc.data().location;

    if (!locationRef) {
      throw new Error("Location reference not found in opportunity document");
    }

    // console.log("locationRef", locationRef);

    // 1. Create initial rotation document
    const initialRotation = {
      locationId: locationRef,
      opportunityId: doc(db, "opps", opportunityId),
      studentId: doc(db, "users", studentId),
      status: "pending",
      currentStage: 1,
      createdAt: Timestamp.now(),
    };

    const rotationRef = await addDoc(
      collection(db, "rotations"),
      initialRotation
    );
    const rotationId = rotationRef.id;

    // 3. Get lifecycles for this location
    const lifecyclesQuery = query(
      collection(db, "lifecycles"),
      where("location", "==", locationRef) // Ensure this matches your Firestore field
    );
    const lifecycleSnapshot = await getDocs(lifecyclesQuery);

    // console.log("lifecycleSnapshot", lifecycleSnapshot);

    if (lifecycleSnapshot.empty) {
      // console.log("No lifecycle found for this location");
      const locationDoc = await getDoc(locationRef);
      // console.log(
      //   "Location:",
      //   locationDoc.exists()
      //     ? { id: locationDoc.id, ...locationDoc.data() }
      //     : null
      // );
      throw new Error("No lifecycle found for this location");
    }

    // Get the first lifecycle (assuming one lifecycle per location)
    const lifecycleDoc = lifecycleSnapshot.docs[0];
    const lifecycle = lifecycleDoc.data();

    // 4. Get first stage and its tasks
    const firstStage = lifecycle.stages.find((stage) => stage.order === 1);
    if (!firstStage) {
      throw new Error("No initial stage found in lifecycle");
    }

    // 5. Create task instances in all-tasks collection
    const taskPromises = firstStage.tasks.map(async (taskRef) => {
      const taskDoc = await getDoc(taskRef);
      const taskData = taskDoc.data();

      return addDoc(collection(db, "all-tasks"), {
        rotationId: doc(db, "rotations", rotationId),
        taskId: taskRef,
        stageName: firstStage.name,
        stageOrder: firstStage.order,
        status: "pending",
        name: taskData.name,
        questions: taskData.questions,
        answers: [],
        createdAt: Timestamp.now(),
      });
    });

    await Promise.all(taskPromises);

    // 6. Update rotation with lifecycle and stage info
    await updateDoc(rotationRef, {
      lifecycleId: lifecycleDoc.id,
      currentStageName: firstStage.name,
      currentStageOrder: firstStage.order,
    });

    // 7. Handle file uploads and create form submission
    const fileUrls = {};
    // console.log(Object.keys(files));
    if (Object.keys(files).length > 0) {
      // Only process files if they exist
      for (const [index, file] of Object.entries(files)) {
        if (file) {
          const storageRef = ref(
            storage,
            `form-uploads/${opportunityId}/${studentId}/${file.name}`
          );
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          fileUrls[index] = url;
        }
      }
    }

    // 8. Create form submission document
    const submission = {
      rotationId: doc(db, "rotations", rotationId),
      opportunityId: doc(db, "opps", opportunityId),
      studentId: doc(db, "users", studentId),
      formId: doc(db, "forms", formId),
      answers: formData.questions.map((question, index) => ({
        question: question.text,
        answer: question.requiresFile
          ? fileUrls[index] || ""
          : answers[index] || "", // Handle case where file or answer is missing
        isFile: question.requiresFile,
      })),
      status: "pending",
      createdAt: Timestamp.now(),
    };

    const submissionRef = await addDoc(
      collection(db, "form-submissions"),
      submission
    );

    return {
      rotationId,
      submissionId: submissionRef.id,
    };
  } catch (error) {
    console.error("Error submitting form:", error);
    throw error;
  }
};

// Add this new function to fetch form submission data
export const getFormSubmissionByRotationId = async (rotationId) => {
  try {
    const q = query(
      collection(db, "form-submissions"),
      where("rotationId", "==", doc(db, "rotations", rotationId))
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const submissionDoc = querySnapshot.docs[0];
    const submissionData = submissionDoc.data();

    // Get related documents
    const [opportunityDoc, studentDoc] = await Promise.all([
      getDoc(submissionData.opportunityId),
      getDoc(submissionData.studentId),
    ]);

    return {
      id: submissionDoc.id,
      ...submissionData,
      opportunity: opportunityDoc.exists()
        ? { id: opportunityDoc.id, ...opportunityDoc.data() }
        : null,
      student: studentDoc.exists()
        ? { id: studentDoc.id, ...studentDoc.data() }
        : null,
    };
  } catch (error) {
    console.error("Error fetching form submission:", error);
    throw error;
  }
};

export const getStageDetails = async (rotationId) => {
  try {
    // Get rotation details
    const rotationDoc = await getDoc(doc(db, "rotations", rotationId));
    if (!rotationDoc.exists()) {
      throw new Error("Rotation not found");
    }
    const rotationData = rotationDoc.data();

    // Get lifecycle details to get all stages
    const lifecycleDoc = await getDoc(
      doc(db, "lifecycles", rotationData.lifecycleId)
    );
    const lifecycleData = lifecycleDoc.data();

    // Get all tasks for this rotation
    const q = query(
      collection(db, "all-tasks"),
      where("rotationId", "==", doc(db, "rotations", rotationId))
    );
    const querySnapshot = await getDocs(q);

    const tasksData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Group tasks by stage
    const stagesTasks = {};
    tasksData.forEach((task) => {
      if (!stagesTasks[task.stageName]) {
        stagesTasks[task.stageName] = [];
      }
      stagesTasks[task.stageName].push(task);
    });

    // Create stages array with tasks and additional properties
    const stages = await Promise.all(
      lifecycleData.stages.map(async (stage) => {
        let evaluationCompleted = false;

        if (stage.evaluation) {
          // Try to fetch evaluation status
          const evaluationQuery = query(
            collection(db, "evaluations"),
            where("rotationId", "==", doc(db, "rotations", rotationId))
          );
          const evaluationSnapshot = await getDocs(evaluationQuery);

          if (!evaluationSnapshot.empty) {
            const evaluationDoc = evaluationSnapshot.docs[0];
            evaluationCompleted = evaluationDoc.data().status === "completed";
          }
        }

        return {
          ...stage,
          isCurrentStage: stage.name === rotationData.currentStageName,
          isCompleted: stage.order < rotationData.currentStageOrder,
          tasks: stagesTasks[stage.name] || [],
          evaluation: stage.evaluation || false,
          evaluationId: stage.evaluationId || null,
          evaluationCompleted,
          order: stage.order,
        };
      })
    );

    return {
      rotation: { id: rotationDoc.id, ...rotationData },
      stages: stages,
    };
  } catch (error) {
    console.error("Error fetching stage details:", error);
    throw error;
  }
};

export const updateTaskStatus = async (rotationId, selectedTaskIds, files) => {
  try {
    // Start a batch
    const batch = writeBatch(db);

    // Update each selected task
    for (const taskId of selectedTaskIds) {
      const taskRef = doc(db, "all-tasks", taskId);

      // Handle file uploads if any
      const taskFiles = {};
      for (const [key, file] of Object.entries(files)) {
        const [currentTaskId, questionIndex] = key.split("-");
        if (currentTaskId === taskId && file) {
          const storageRef = ref(
            storage,
            `task-uploads/${rotationId}/${taskId}/${questionIndex}/${file.name}`
          );
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          taskFiles[questionIndex] = url;
        }
      }

      // Update task document
      batch.update(taskRef, {
        status: "completed",
        completedAt: Timestamp.now(),
        fileUrls: taskFiles,
      });
    }

    // Commit the batch
    await batch.commit();

    return true;
  } catch (error) {
    console.error("Error updating task status:", error);
    throw error;
  }
};

export const handleCardAccept = async (cardId) => {
  try {
    // Get current rotation data
    const rotationRef = doc(db, "rotations", cardId);
    const rotationDoc = await getDoc(rotationRef);
    const rotationData = rotationDoc.data();

    const oppsRef = rotationData.opportunityId;
    const oppsDoc = await getDoc(oppsRef);
    const oppsData = oppsDoc.data();

    // Get lifecycle data
    const lifecycleRef = doc(db, "lifecycles", rotationData.lifecycleId);
    const lifecycleDoc = await getDoc(lifecycleRef);
    const lifecycleData = lifecycleDoc.data();

    // Find current stage index
    const currentStageIndex = lifecycleData.stages.findIndex(
      (stage) => stage.name === rotationData.currentStageName
    );

    // Check if there's a next stage
    if (currentStageIndex < lifecycleData.stages.length - 1) {
      const nextStage = lifecycleData.stages[currentStageIndex + 1];

      // Start a batch write
      const batch = writeBatch(db);

      // Update rotation with new stage info
      batch.update(rotationRef, {
        currentStageName: nextStage.name,
        currentStageOrder: nextStage.order,
        status: rotationData.status + 1,
        updatedAt: Timestamp.now(),
      });

      // If next stage requires evaluation, create one
      if (nextStage.evaluation === true) {
        const evaluationData = {
          studentId: rotationData.studentId,
          evaluatorId: rotationData.evaluator || null, // You might want to set this based on your requirements
          rotationId: rotationRef,
          locationId: oppsData.location,
          opportunityId: rotationData.opportunityId,
          evaluationDate: Timestamp.now(),
          categories: [], // Will be populated when evaluation is completed
          overallFeedback: "",
          evaluationScore: 0,
          status: "pending",
        };

        // Create new evaluation document
        const newEvaluationRef = doc(collection(db, "evaluations"));
        batch.set(newEvaluationRef, {
          ...evaluationData,
          createdAt: Timestamp.now(),
        });

        // Update the stage with the evaluation ID
        batch.update(rotationRef, {
          currentStageEvaluationId: newEvaluationRef.id,
        });
      }

      // Create task instances for the next stage
      if (nextStage.tasks && nextStage.tasks.length > 0) {
        for (const taskRef of nextStage.tasks) {
          const taskDoc = await getDoc(taskRef);
          const taskData = taskDoc.data();

          // Create new task instance
          const newTaskRef = doc(collection(db, "all-tasks"));
          batch.set(newTaskRef, {
            rotationId: rotationRef,
            taskId: taskRef,
            stageName: nextStage.name,
            stageOrder: nextStage.order,
            status: "pending",
            name: taskData.name,
            questions: taskData.questions || [],
            createdAt: Timestamp.now(),
          });
        }
      }

      // Commit all changes
      await batch.commit();
      return true;
    } else {
      // This is the final stage
      await updateDoc(rotationRef, {
        status: "completed",
        completedAt: Timestamp.now(),
      });
      return true;
    }
  } catch (error) {
    console.error("Error accepting card:", error);
    throw error;
  }
};

export const checkStageCompletion = async (rotationId) => {
  try {
    // Get current rotation data
    const rotationDoc = await getDoc(doc(db, "rotations", rotationId));
    const rotationData = rotationDoc.data();

    // Get all tasks for current stage
    const tasksQuery = query(
      collection(db, "all-tasks"),
      where("rotationId", "==", doc(db, "rotations", rotationId)),
      where("stageName", "==", rotationData.currentStageName)
    );

    const tasksDocs = await getDocs(tasksQuery);
    const tasks = tasksDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    // Check if all tasks are completed
    const allTasksCompleted =
      tasks.length > 0 && tasks.every((task) => task.status === "completed");

    return allTasksCompleted;
  } catch (error) {
    console.error("Error checking stage completion:", error);
    throw error;
  }
};

// Get base by user ID
export const getBaseByUserId = async (userId) => {
  const userRef = doc(db, "users", userId);
  const querySnapshot = await getDocs(
    query(collection(db, "base"), where("users", "array-contains", userRef))
  );

  if (querySnapshot.empty) {
    return null;
  }

  const baseDoc = querySnapshot.docs[0];
  return { id: baseDoc.id, ...baseDoc.data() };
};

// Get locations by base ID
export const getLocationsByBaseId = async (baseId) => {
  const baseRef = doc(db, "base", baseId);
  const baseDoc = await getDoc(baseRef);
  const baseData = baseDoc.exists() ? baseDoc.data() : null;

  if (!baseData || !baseData.locations) {
    return [];
  }

  const locationPromises = baseData.locations.map(async (location) => {
    const locationDoc = await getDoc(doc(db, "locations", location.id));
    return locationDoc.exists()
      ? { id: locationDoc.id, ...locationDoc.data() }
      : null;
  });

  const locations = await Promise.all(locationPromises);
  return locations.filter((location) => location !== null);
};

// Get lifecycles by location ID
export const getLifecyclesByLocation = async (locationId) => {
  try {
    const lifecyclesQuery = query(
      collection(db, "lifecycles"),
      where("locationId", "==", locationId)
    );

    const querySnapshot = await getDocs(lifecyclesQuery);
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error("Error fetching lifecycles:", error);
    throw error;
  }
};

// Get rotations by lifecycle ID
export const getRotationsByLifecycleId = async (lifecycleId) => {
  try {
    const rotationsQuery = query(
      collection(db, "rotations"),
      where("lifecycleId", "==", lifecycleId)
    );
    const querySnapshot = await getDocs(rotationsQuery);
    const rotations = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data();

        // Get opportunity data
        const opportunityDoc = await getDoc(data.opportunityId);
        // console.log("opportunityDoc", opportunityDoc);
        const opportunityData = opportunityDoc.exists()
          ? {
              id: opportunityDoc.id,
              ...opportunityDoc.data(),
            }
          : null;

        // Get location data from opportunity
        // console.log("opportunityData", opportunityData);
        const locationDoc = await getDoc(opportunityData.location);
        // console.log("locationDoc", locationDoc);
        const locationData = locationDoc.exists()
          ? {
              id: locationDoc.id,
              ...locationDoc.data(),
            }
          : null;

        // Get student data
        const studentDoc = await getDoc(data.studentId);
        const studentData = studentDoc.exists()
          ? {
              id: studentDoc.id,
              ...studentDoc.data(),
            }
          : null;

        // Return combined data with IDs
        return {
          id: doc.id,
          rotationId: doc.id,
          ...data,
          opps: {
            ...opportunityData,
            id: opportunityDoc.id,
          },
          location: {
            ...locationData,
            id: locationDoc.id,
          },
          student: {
            ...studentData,
            id: studentDoc.id,
          },
        };
      })
    );

    return rotations;
  } catch (error) {
    console.error("Error getting rotations by lifecycle ID: ", error);
    throw error;
  }
};

// Get all permissions except super_admin
export const getAllPermissions = async () => {
  try {
    const permissionsCollection = collection(db, "permissions");
    const querySnapshot = await getDocs(permissionsCollection);

    const permissions = [];
    querySnapshot.forEach((doc) => {
      if (doc.id !== "SUPER_ADMIN") {
        permissions.push(doc.id);
        // console.log("permissions", permissions);
      }
    });

    if (Object.keys(permissions).length > 0) {
      return permissions;
    } else {
      // console.log("No permissions found!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching permissions:", error);
    throw error;
  }
};

// Get permissions by type
export const getPermissions = async (type) => {
  try {
    const docRef = doc(db, "permissions", type);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching permissions:", error);
    throw error;
  }
};

// Update permissions by type
export const updatePermissions = async (type, permissions) => {
  try {
    await setDoc(doc(db, "permissions", type), permissions);
    // // console.log("Permissions updated successfully");
  } catch (error) {
    console.error("Error updating permissions:", error);
    throw error;
  }
};

// Get student evaluations with related data
export const getStudentEvaluations = async (studentId) => {
  try {
    // // console.log("studentId", studentId);
    const evaluationsCollection = collection(db, "evaluations");
    const q = query(
      evaluationsCollection,
      where("studentId", "==", doc(db, "users", studentId))
    );
    const querySnapshot = await getDocs(q);

    const evaluations = await Promise.all(
      querySnapshot.docs.map(async (evaluationDoc) => {
        const data = evaluationDoc.data();

        // Get opportunity data
        const opportunityDoc = await getDoc(data.opportunityId);
        const opportunityData = opportunityDoc.data();

        // Get location data
        const locationDoc = await getDoc(data.locationId);
        const locationData = locationDoc.data();

        // Get student data
        const studentDoc = await getDoc(data.studentId);
        const studentData = studentDoc.data();

        return {
          id: evaluationDoc.id,
          ...data,
          opportunity: {
            ...opportunityData,
            id: opportunityDoc.id,
          },
          location: {
            ...locationData,
            id: locationDoc.id,
          },
          student: {
            ...studentData,
            id: studentDoc.id,
          },
        };
      })
    );

    return evaluations;
  } catch (error) {
    console.error("Error getting student evaluations: ", error);
    throw error;
  }
};

// Get location evaluations with related data
export const getLocationEvaluations = async (locationId) => {
  try {
    const evaluationsCollection = collection(db, "evaluations");
    const q = query(
      evaluationsCollection,
      where("locationId", "==", doc(db, "locations", locationId))
    );
    const querySnapshot = await getDocs(q);

    const evaluations = await Promise.all(
      querySnapshot.docs.map(async (evaluationDoc) => {
        const data = evaluationDoc.data();

        // Get opportunity data
        const opportunityDoc = await getDoc(data.opportunityId);
        const opportunityData = opportunityDoc.data();

        // Get location data
        const locationDoc = await getDoc(data.locationId);
        const locationData = locationDoc.data();

        // Get student data
        const studentDoc = await getDoc(data.studentId);
        const studentData = studentDoc.data();

        return {
          id: evaluationDoc.id,
          ...data,
          opportunity: {
            ...opportunityData,
            id: opportunityDoc.id,
          },
          location: {
            ...locationData,
            id: locationDoc.id,
          },
          student: {
            ...studentData,
            id: studentDoc.id,
          },
        };
      })
    );

    return evaluations;
  } catch (error) {
    console.error("Error getting location evaluations: ", error);
    throw error;
  }
};

export const addContactUser = async (userData) => {
  try {
    const usersCollection = collection(db, "contact-users");
    const docRef = await addDoc(usersCollection, userData);
    // console.log("Document written with ID: ", docRef.id);
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

export const getContactUsers = async () => {
  const usersCollection = collection(db, "contact-users");
  const querySnapshot = await getDocs(usersCollection);
  return querySnapshot.docs.map((doc) => doc.data());
};

// Get all teachers
export const getAllTeachers = async () => {
  const trainerRef = doc(db, "permissions", "TRAINER");
  const q = query(collection(db, "users"), where("role", "==", trainerRef));
  const querySnapshot = await getDocs(q);
  const teachers = [];
  querySnapshot.forEach((doc) => {
    teachers.push({ id: doc.id, ...doc.data() });
  });
  // console.log("Teachers:", teachers);
  return teachers;
};
// Get teachers by base ID
export const getTeachersByBaseId = async (baseId) => {
  try {
    // Get the base document to check its users array
    const baseRef = doc(db, "base", baseId);
    const baseDoc = await getDoc(baseRef);
    const baseData = baseDoc.data();

    // Get all users with TRAINER permission that are in the base's users array
    const trainerRef = doc(db, "permissions", "TRAINER");
    const trainersQuery = query(
      collection(db, "users"),
      where("role", "==", trainerRef),
      where(
        "__name__",
        "in",
        baseData.users.map((userRef) => userRef.id)
      )
    );

    const trainersSnapshot = await getDocs(trainersQuery);
    const teachers = trainersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // console.log("Teachers:", teachers);
    return teachers;
  } catch (error) {
    console.error("Error getting teachers by base ID:", error);
    throw error;
  }
};

// Get user registrations opportunity by user ID
export const getUserRegistrations = async (userId) => {
  try {
    const rotationsRef = collection(db, "rotations");
    const userRef = doc(db, "users", userId);
    const q = query(rotationsRef, where("studentId", "==", userRef));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => doc.data().opportunityId.id);
  } catch (error) {
    console.error("Error getting user registrations:", error);
    throw error;
  }
};

// Update opportunity seats
export const updateOpportunitySeats = async (opportunityRef) => {
  try {
    const oppDoc = await getDoc(opportunityRef);
    const currentSeats = oppDoc.data().seats;
    // console.log("Current Seats:", currentSeats);
    await updateDoc(opportunityRef, {
      seats: currentSeats - 1,
    });
  } catch (error) {
    console.error("Error updating opportunity seats:", error);
    throw error;
  }
};

export const getLifecyclesByBaseId = async (baseId) => {
  try {
    // Get base's locations first
    const baseRef = doc(db, "base", baseId);
    const baseDoc = await getDoc(baseRef);
    const baseData = baseDoc.data();

    if (!baseData?.locations?.length) {
      return [];
    }

    // Get all lifecycles for these locations
    const lifecyclesCollection = collection(db, "lifecycles");
    const locationRefs = baseData.locations.map((loc) =>
      doc(db, "locations", loc.id)
    );
    const querySnapshot = await getDocs(
      query(lifecyclesCollection, where("location", "in", locationRefs))
    );

    // Map through documents and resolve task references
    const lifecycles = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data();

        // Get location data
        const locationDoc = await getDoc(data.location);
        const locationData = locationDoc.exists() ? locationDoc.data() : null;

        // Resolve tasks for each stage
        const stages = await Promise.all(
          data.stages.map(async (stage) => {
            const tasks = await Promise.all(
              (stage.tasks || []).map(async (taskRef) => {
                const taskDoc = await getDoc(taskRef);
                return taskDoc.exists()
                  ? { id: taskDoc.id, ...taskDoc.data() }
                  : null;
              })
            );

            return {
              ...stage,
              tasks: tasks.filter((task) => task !== null), // Remove any null tasks
            };
          })
        );

        return {
          id: doc.id,
          ...data,
          location: { id: locationDoc.id, ...locationData },
          stages,
        };
      })
    );

    return lifecycles;
  } catch (error) {
    console.error("Error getting lifecycles by base ID: ", error);
    throw error;
  }
};

// Get trainer evaluations
export const getTrainerEvaluations = async (trainerId) => {
  try {
    // 1. Get the base where trainer is assigned
    const baseQuery = query(
      collection(db, "base"),
      where("users", "array-contains", doc(db, "users", trainerId))
    );
    const baseSnapshot = await getDocs(baseQuery);

    if (baseSnapshot.empty) {
      // console.log("No base found for trainer:", trainerId);
      return [];
    }

    // 2. Get all locations in the base
    const baseDoc = baseSnapshot.docs[0];
    const baseLocations = baseDoc.data().locations || [];
    const locationRefs = baseLocations.map((loc) =>
      doc(db, "locations", loc.id)
    );

    if (locationRefs.length === 0) {
      // console.log("No locations found in base");
      return [];
    }

    // 3. Get all opportunities in these locations that have the trainer in teachers array
    const oppsQuery = query(
      collection(db, "opps"),
      where("location", "in", locationRefs),
      where("teachers", "array-contains", trainerId)
    );
    const oppsSnapshot = await getDocs(oppsQuery);
    const oppRefs = oppsSnapshot.docs.map((doc) => doc.ref);

    if (oppRefs.length === 0) {
      // console.log("No opportunities found for trainer");
      return [];
    }

    // 4. Get all evaluations that have these opportunity IDs
    const evaluationsQuery = query(
      collection(db, "evaluations"),
      where("opportunityId", "in", oppRefs)
    );
    const evaluationsSnapshot = await getDocs(evaluationsQuery);

    // 5. Map evaluation data with resolved references
    const evaluations = await Promise.all(
      evaluationsSnapshot.docs.map(async (evaluationDoc) => {
        const data = evaluationDoc.data();

        // Get opportunity data
        const opportunityDoc = await getDoc(data.opportunityId);
        const opportunityData = opportunityDoc.exists()
          ? opportunityDoc.data()
          : null;

        // Get location data
        const locationDoc = await getDoc(data.locationId);
        const locationData = locationDoc.exists() ? locationDoc.data() : null;

        // Get student data
        const studentDoc = await getDoc(data.studentId);
        const studentData = studentDoc.exists() ? studentDoc.data() : null;

        return {
          id: evaluationDoc.id,
          ...data,
          opportunity: {
            id: opportunityDoc.id,
            ...opportunityData,
          },
          location: {
            id: locationDoc.id,
            ...locationData,
          },
          student: {
            id: studentDoc.id,
            ...studentData,
          },
          categories: data.categories || [],
          evaluationScore: data.evaluationScore || "NaN",
          status: data.status || "pending",
          evaluationDate: data.evaluationDate,
          overallFeedback: data.overallFeedback || "",
          completedAt: data.completedAt,
          createdAt: data.createdAt,
        };
      })
    );

    return evaluations;
  } catch (error) {
    console.error("Error getting trainer evaluations:", error);
    throw error;
  }
};

// Get user registrations opportunity by user ID
export const getUserRegisteredOpportunities = async (userId) => {
  try {
    // Create a query to get all rotations for this user
    const rotationsQuery = query(
      collection(db, "rotations"),
      where("studentId", "==", doc(db, "users", userId))
    );

    // Get all rotations
    const querySnapshot = await getDocs(rotationsQuery);

    // Get all opportunities data
    const registrations = await Promise.all(
      querySnapshot.docs.map(async (rotationDoc) => {
        const rotationData = rotationDoc.data();

        // Get the opportunity data
        const oppDoc = await getDoc(rotationData.opportunityId);
        if (oppDoc.exists()) {
          return {
            id: oppDoc.id,
            ...oppDoc.data(),
            rotationId: rotationDoc.id,
            rotationStatus: rotationData.status,
          };
        }
        return null;
      })
    );

    // Filter out any null values and return
    return registrations.filter((reg) => reg !== null);
  } catch (error) {
    console.error("Error getting user registrations:", error);
    throw error;
  }
};

// Track user interest in opportunities
export const trackOpportunityInterest = async (opportunityId, userData) => {
  try {
    const userId = userData.userId ? doc(db, "opps", opportunityId) : null;
    // Add document to opportunity-interests collection
    await addDoc(collection(db, "opportunity-interests"), {
      opportunityId: doc(db, "opps", opportunityId),
      userId: userId,
      timestamp: Timestamp.now(),
      source: "our-website", // Where the interest was recorded from
      deviceInfo: {
        userAgent: window.navigator.userAgent,
        platform: window.navigator.platform,
        language: window.navigator.language,
      },
      // Add any additional tracking data here
      interactionType: "apply", // Could be view, click, apply etc
      userName: userData.name,
      userEmail: userData.email,
      userPhone: userData.phone,
    });
  } catch (error) {
    console.error("Error tracking opportunity interest:", error);
    throw error;
  }
};


// Handle Excel file data
export const handleExcelData = async () => {
  try {
    const response = await fetch('/assets/OppsData.xlsx');
    const blob = await response.blob();
    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true, cellStyles: true });

      // Get the first worksheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Function to extract image from cell
      const extractImage = async (worksheet, cellRef) => {
        if (!worksheet[cellRef] || !worksheet[cellRef].l) return null;
        
        const imageId = worksheet[cellRef].l.Target.slice(1); // Remove '#' from reference
        const image = workbook.Images[imageId];
        
        if (image) {
          // Convert image data to base64
          const base64String = `data:${image.type};base64,${image.data}`;
          
          // Convert base64 to blob for Firebase upload
          const response = await fetch(base64String);
          const blob = await response.blob();
          
          // Upload to Firebase Storage and get URL
          const storage = getStorage();
          const imageRef = ref(storage, `logos/${Date.now()}_${imageId}.${image.type.split('/')[1]}`);
          await uploadBytes(imageRef, blob);
          const downloadURL = await getDownloadURL(imageRef);
          
          return downloadURL;
        }
        return null;
      };

      // Convert to JSON with headers as keys
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        raw: true,
        defval: null // Use null for empty cells
      });

      // Process each row to handle images
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const rowIndex = i + 2; // Adding 2 because of header row and 1-based indexing
        const logoCell = `P${rowIndex}`; // Assuming Logo is in column P, adjust if different
        
        // Extract and upload image if exists
        const logoUrl = await extractImage(worksheet, logoCell);
        if (logoUrl) {
          row.Logo = logoUrl; // Store the Firebase Storage URL in the row data
        }
      }

      // Check if jsonData has valid structure
      if (!Array.isArray(jsonData) || jsonData.length === 0) {
        throw new Error("No valid data found in the worksheet.");
      }

      // Function to extract coordinates from the Map URL
      const extractCoordinatesFromUrl = (url) => {
        try {
          const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
          const match = url.match(regex);
          
          if (match) {
            const [, latitude, longitude] = match;
            return {
              latitude: parseFloat(latitude),
              longitude: parseFloat(longitude)
            };
          }
          return null;
        } catch (error) {
          console.error("Error parsing coordinates:", error);
          return null;
        }
      };

      // Group data by location
      const groupedData = jsonData.reduce((acc, item) => {
        if (!item.Location_en) return acc;

        const locationKey = item.Location_en;
        
        if (!acc[locationKey]) {
          const coordinates = extractCoordinatesFromUrl(item.Map);
          acc[locationKey] = {
            createdAt: Timestamp.now(),
            name: item.Location_en,
            name_ar: item.Location_ar,
            location: {
              latitude: coordinates ? coordinates.latitude : null,
              longitude: coordinates ? coordinates.longitude : null
            },
            mapURL: item.Map || '',
            opps: [],
            isDummy: true,
            Logo: item.ImageUrl || '' // Use the processed Logo URL from earlier
          };
        }

        acc[locationKey].opps.push({
          createdAt: Timestamp.now(),
          major_en: item.Major_en.replace(/^\s+|\s+$/gm,''), 
          major_ar: item.Major_ar.replace(/^\s+|\s+$/gm,''), 
          name: item.Name_en.replace(/^\s+|\s+$/gm,''), 
          name_ar: item.Name_ar.replace(/^\s+|\s+$/gm,''), 
          description_en: item.Description_en.replace(/^\s+|\s+$/gm,''), 
          description_ar: item.Description_ar.replace(/^\s+|\s+$/gm,''), 
          duration_en: item.Duration_en.replace(/^\s+|\s+$/gm,''), 
          duration_ar: item.Duration_ar.replace(/^\s+|\s+$/gm,''), 
          price: item.Price, 
          fee: parseInt(item.Fee), 
          // category: item.Category, 
          isDummy: true 
        });

        return acc;
      }, {});

      // Convert the grouped data object to an array
      const resultArray = Object.values(groupedData);

      // Use a batch to handle Firestore writes
      const batch = writeBatch(db);

      for (const location of resultArray) {
        // Create a document in the base collection
        let baseData = {
          createdAt: Timestamp.now(),
          image: location.Logo,
          locations: [],
          name: location.name,
          name_ar: location.name_ar,
          isDummy: true,
        }
        const baseRef = doc(collection(db, 'base')); // Create a new base document reference
        batch.set(baseRef, {
          ...baseData
        });

        let locationData = {
          createdAt: Timestamp.now(),
          base: baseRef,
          image: location.Logo, // This will now be the Firebase Storage URL
          location: new GeoPoint(location.location.latitude, location.location.longitude),
          name: location.name,
          name_ar: location.name_ar,
          isDummy: true,
        }
        // Create a document in the location collection
        const locationRef = doc(collection(db, 'locations')); // Create a new location document reference
        batch.set(locationRef, {
          ...locationData
        });

        // Add the location reference to the base
        baseData.locations.push(locationRef);

        // Create opps in the opps collection
        for (const opp of location.opps) {
          let oppData = {
            base: baseRef,
            baseId: baseRef.id,
            createdAt: Timestamp.now(),
            description_ar: opp.description_ar,
            description_en: opp.description_en,
            duration_ar: opp.duration_ar,
            duration_en: opp.duration_en,
            fee: opp.fee,
            location: locationRef,
            major_ar: opp.major_ar,
            major_en: opp.major_en,
            name: opp.name,
            name_ar: opp.name_ar,
            price: opp.price,
            isDummy: true,
            startDate: Timestamp.now(),
            endDate: new Timestamp(Timestamp.now().seconds + (180 * 24 * 60 * 60), 0), // Add 6 months (180 days)
            seats: 10,
            teachers: [],
            // category: opp.category
          }
          console.log(oppData);
          const oppRef = doc(collection(db, 'opps')); // Create a new opp document reference
          batch.set(oppRef, {
            ...oppData
          });
        }

        // Update the base document with the new location reference
        batch.update(baseRef, {
          locations: arrayUnion(locationRef) // Add the new location reference to the base's locations array
        });
      }

      // Commit the batch
      await batch.commit();
      console.log('Grouped Excel data by location:', resultArray); // Log grouped data
      return resultArray; // Return grouped data
    };

    reader.readAsArrayBuffer(blob);

  } catch (error) {
    console.error("Error processing Excel file:", error);
    throw error; // Rethrow the error to handle it upstream if needed
  }
};

// Remove dummy data
export const removeDummyData = async () => {
  try {
    // Get all dummy bases
    const basesSnapshot = await getDocs(
      query(collection(db, "base"), where("isDummy", "==", true))
    );

    // Get all dummy locations 
    const locationsSnapshot = await getDocs(
      query(collection(db, "locations"), where("isDummy", "==", true))
    );

    // Get all dummy opps
    const oppsSnapshot = await getDocs(
      query(collection(db, "opps"), where("isDummy", "==", true))
    );

    const batch = writeBatch(db);

    // Delete dummy bases
    basesSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Delete dummy locations
    locationsSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Delete dummy opps
    oppsSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log("Successfully removed all dummy data");
  } catch (error) {
    console.error("Error removing dummy data:", error);
    throw error;
  }
};


export const countVisitors = async (utmSource = 'website') => {
  // Reference to the visitor count documents
  const visitorsRef = doc(db, "siteStats", "visitorCount");
  const returningVisitorsRef = doc(db, "siteStats", "returningVisitorCount");
  const sourceStatsRef = doc(db, "siteStats", "trafficSources");

  // Normalize the source to lowercase
  const source = utmSource ? utmSource.toLowerCase() : 'website';

  try {
    // Get the traffic sources document
    const sourceDoc = await getDoc(sourceStatsRef);
    const sourceStats = sourceDoc.exists() ? sourceDoc.data() : {
      tiktok: 0,
      instagram: 0,
      facebook: 0,
      twitter: 0,
      snapchat: 0,
      website: 0,
      hospitals: 0
    };

    // Increment the count for the specific source
    if (source in sourceStats) {
      sourceStats[source] += 1;
    } else {
      sourceStats.website += 1; // Default to website if source is not recognized
    }

    // Update traffic sources
    await setDoc(sourceStatsRef, sourceStats);

    // Check if the visitor has already been counted
    const hasVisited = localStorage.getItem('hasVisited');

    if (!hasVisited) {
      // If not counted yet, increment the new visitor count
      const visitorDoc = await getDoc(visitorsRef);

      if (!visitorDoc.exists()) {
        await setDoc(visitorsRef, { count: 1 });
      } else {
        const currentCount = visitorDoc.data().count || 0;
        await setDoc(visitorsRef, { count: currentCount + 1 }, { merge: true });
      }

      // Set the flag in localStorage to indicate the visitor has been counted
      localStorage.setItem('hasVisited', 'true');
      localStorage.setItem('source', source); // Store the source
    } else {
      // Increment returning visitor count
      const returningDoc = await getDoc(returningVisitorsRef);

      if (!returningDoc.exists()) {
        await setDoc(returningVisitorsRef, { count: 1 });
      } else {
        const currentCount = returningDoc.data().count || 0;
        await setDoc(returningVisitorsRef, { count: currentCount + 1 }, { merge: true });
      }
    }
  } catch (error) {
    console.error("Error counting visitors:", error);
    throw error;
  }
};

// Add a new function to get traffic source statistics
export const getTrafficSourceStats = async () => {
  try {
    const sourceStatsRef = doc(db, "siteStats", "trafficSources");
    const sourceDoc = await getDoc(sourceStatsRef);
    
    if (!sourceDoc.exists()) {
      return {
        tiktok: 0,
        instagram: 0,
        facebook: 0,
        twitter: 0,
        snapchat: 0,
        website: 0
      };
    }
    
    return sourceDoc.data();
  } catch (error) {
    console.error("Error getting traffic source stats:", error);
    throw error;
  }
};

// Get visitor stats
export const getVisitorStats = async () => {
  const visitorDoc = await getDoc(doc(db, "siteStats", "visitorCount"));
  const returningVisitorDoc = await getDoc(doc(db, "siteStats", "returningVisitorCount"));
  
  return {
    visitorCount: visitorDoc.exists() ? visitorDoc.data().count || 0 : 0,
    returningVisitorCount: returningVisitorDoc.exists() ? returningVisitorDoc.data().count || 0 : 0
  };
};

// Get opportunity interest stats
export const getOpportunityInterests = async () => {
  try {
    const interestsRef = collection(db, "opportunity-interests");
    const q = query(interestsRef, orderBy("timestamp", "desc")); // Order by timestamp descending
    const interestsSnapshot = await getDocs(q);
    
    if (interestsSnapshot.empty) {
      return [];
    }

    // Map through the already-ordered documents
    const promises = interestsSnapshot.docs.map(async doc => {
      const data = doc.data();
      const oppData = await getOppById(data.opportunityId);
      
      return {
        userName: data?.userName || '',
        userEmail: data?.userEmail || '',
        interactionType: data?.interactionType || '',
        timestamp: data?.timestamp?.toDate?.() || new Date(), // Convert Firestore timestamp to JS Date
        opportunityId: data?.opportunityId || '',
        source: data?.source || '',
        userId: data?.userId || '',
        userPhone: data?.userPhone || '',
        deviceInfo: data?.deviceInfo || '',
        opportunity: oppData || null,
        location: oppData?.location?.name || null,
        price: oppData?.price || null
      };
    });

    return Promise.all(promises);

  } catch (error) {
    console.error("Error fetching opportunity interests:", error);
    throw new Error("Failed to fetch opportunity interests");
  }
};


// Get opportunity by ID
export const getOppById = async (oppRef) => {
  if (!oppRef) {
    return null;
  }

  try {
    // Extract ID from reference string (e.g. "opps/0uspxH59FTO5R6OMbCSu")
    const oppId = oppRef.id;
    
    // Get opportunity document
    const oppDocRef = doc(db, 'opps', oppId);
    const oppDoc = await getDoc(oppDocRef);

    if (!oppDoc.exists()) {
      return null;
    }

    const data = oppDoc.data();
    // Get location data
    const locationData = await getDoc(data.location);

    const oppsData = {
      id: oppDoc.id,
      ...data,
      name: data.name,
      startDate: data.startDate.toDate().toISOString().slice(0, 16),
      endDate: data.endDate.toDate().toISOString().slice(0, 16),
      location: locationData.exists() ? { id: locationData.id, ...locationData.data() } : null,
      form: data.form?.id || "",
      baseId: data.base.id
    }
    return oppsData;

  } catch (error) {
    throw new Error("Failed to fetch opportunity");
  }
};
